import {
  Box,
  Button,
  NumberBadge,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import { Authorized } from 'modules/core/components/Authorized'
import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import useDownloadTemplate from 'modules/employees/hooks/useDownloadTemplate'
import {
  StartImporterProcessError,
  useStartImporterProcess,
} from 'modules/employees/hooks/useStartImporterProcess'
import { WizardData } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/WizardData'

const isCompletedStep = (data: WizardData) => {
  return !!data.file && !data.fileError
}

const downloadPath = (importer_uuid: string) => `/importers/download_template/${importer_uuid}`

const UploadFileStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const { startImporterProcess } = useStartImporterProcess()
  const { downloading, onDownload, error, value } = useDownloadTemplate({
    path: downloadPath(data.importerUuid),
  })

  const handleOnDownload = () => {
    onDownload()
    track(trackingEvents.importEmployees.uploadFileStep.downloadTemplate, {
      error: error && value?.toString(),
    })
  }

  useOnNext(async (wizardData: WizardData) => {
    await startImporterProcess({
      variables: {
        file: wizardData.file,
        importerUuid: wizardData.importerUuid,
      },
      onSuccess: ({ importerProcessUuid }) => {
        onDataChanged({
          ...wizardData,
          importerProcessUuid: importerProcessUuid || '',
          resetSteps: false,
        })
        track(trackingEvents.importEmployees.uploadFileStep.next)
      },
      onFail: ({ type, message, flash }: StartImporterProcessError) => {
        if (flash) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('importer_process.upload_file_step.cannot_create_process'),
          })
        }

        onDataChanged({
          ...wizardData,
          fileError: { type, message, flash },
        })

        throw new Error()
      },
    })
  })

  const handleFileChange = (file: File | undefined) => {
    onDataChanged({
      ...data,
      file,
      fileError: undefined,
      mappedFields: [],
      originalFileContent: undefined,
      resetSteps: true,
    })
    track(trackingEvents.importEmployees.uploadFileStep.attachFile)
  }

  return (
    <Box flexDirection="column" gap="s40">
      <Text.Header2>{i18n.t('importer_process.upload_file_step.header')}</Text.Header2>
      <Box flexDirection="column" gap="s24" gridColSpan={{ xxs: 12 }}>
        <Box flexDirection="row" gap="s8">
          <NumberBadge label={1} />
          <Text.Normal weight="semibold">
            {i18n.t('importer_process.upload_file_step.fields.file.label')}
          </Text.Normal>
        </Box>
        <Box>
          <Authorized subscription={subscription}>
            <DropzoneBox
              onDrop={(files: File[]) => handleFileChange(files[0])}
              iconName="common-file-text-upload"
              message={
                data.file?.name || i18n.t('importer_process.upload_file_step.fields.file.message')
              }
              description={i18n.t('importer_process.upload_file_step.fields.file.description')}
              multiple={false}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                'application/vnd.ms-excel': [],
              }}
              error={!!data.fileError?.message}
              maxSize={4}
            />
          </Authorized>
          {!!data.fileError?.message && (
            <Box paddingTop="s8">
              <Callout
                type="critical"
                title={data.fileError.type}
                description={data.fileError.message}
              />
            </Box>
          )}
        </Box>
        <Box>
          <Text.Normal weight="semibold">
            {i18n.t('importer_process.upload_file_step.fields.download_template.title')}
          </Text.Normal>
          <Text.Normal>
            {i18n.t('importer_process.upload_file_step.fields.download_template.description')}
          </Text.Normal>
          <Box paddingTop="s8">
            <Button onClick={handleOnDownload} disabled={downloading} type="positive">
              {i18n.t('importer_process.upload_file_step.fields.download_template.link')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const uploadFileStep = (): WizardStep<WizardData> => ({
  title: i18n.t('importer_process.upload_file_step.title'),
  nextText: i18n.t('importer_process.upload_file_step.next'),
  render: (props) => <UploadFileStep {...props} />,
  isCompleted: (data) => isCompletedStep(data),
})
